import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { History, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type TestimonialStatus = "pending" | "approved" | "published" | "rejected" | "archived";

type AuditRow = {
  id: string;
  testimonial_id: string;
  action: string;
  previous_status: TestimonialStatus | null;
  new_status: TestimonialStatus | null;
  moderator_notes: string | null;
  actor_id: string | null;
  actor_email: string | null;
  created_at: string;
};

const STATUS_TONE: Record<TestimonialStatus, string> = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  approved: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  published: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

const ACTION_LABEL: Record<string, string> = {
  submitted: "Submitted",
  status_changed: "Status changed",
  note_updated: "Notes updated",
};

const fmt = (value: string) =>
  new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const StatusChip = ({ status }: { status: TestimonialStatus | null }) =>
  status ? (
    <Badge variant="outline" className={STATUS_TONE[status]}>
      {status}
    </Badge>
  ) : (
    <span className="text-xs text-muted-foreground">—</span>
  );

const AdminTestimonialAudit = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  const testimonialFilter = params.get("testimonial") ?? "";
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    let request = supabase
      .from("testimonial_audit_log")
      .select(
        "id,testimonial_id,action,previous_status,new_status,moderator_notes,actor_id,actor_email,created_at",
      )
      .order("created_at", { ascending: false })
      .limit(300);

    if (testimonialFilter) request = request.eq("testimonial_id", testimonialFilter);

    const { data, error } = await request;
    setLoading(false);
    if (error) return;

    const list = (data ?? []) as AuditRow[];
    setRows(list);

    const ids = Array.from(new Set(list.map((r) => r.testimonial_id)));
    if (ids.length) {
      const { data: subs } = await supabase
        .from("testimonial_submissions")
        .select("id,full_name")
        .in("id", ids);
      setNames(Object.fromEntries((subs ?? []).map((s) => [s.id, s.full_name])));
    }
  }, [testimonialFilter]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        names[r.testimonial_id] ?? "",
        r.actor_email ?? "",
        r.moderator_notes ?? "",
        r.action,
        r.new_status ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [names, query, rows]);

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to view the testimonial audit log.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>Testimonial Audit Log | Admin</title>
        <meta name="description" content="Internal audit trail of testimonial status changes and moderator notes." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <History className="h-5 w-5" aria-hidden="true" />
            Testimonial audit log
          </h1>
          <p className="text-sm text-muted-foreground">
            Who changed each testimonial&apos;s status, the notes they left, and when.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/admin/testimonials">Back to moderation</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <div className="mb-6 space-y-2">
        <Label htmlFor="audit-search">Search</Label>
        <Input
          id="audit-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by client, moderator, note, or status"
        />
        {testimonialFilter && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              Showing history for {names[testimonialFilter] ?? "one testimonial"}.
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                params.delete("testimonial");
                setParams(params, { replace: true });
              }}
            >
              Show all
            </Button>
          </div>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No audit entries yet.</p>
      ) : (
        <ol className="space-y-3">
          {visible.map((row) => (
            <li key={row.id}>
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 pb-3">
                  <div>
                    <CardTitle className="text-base">
                      {names[row.testimonial_id] ?? "Testimonial"}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {ACTION_LABEL[row.action] ?? row.action} · {fmt(row.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusChip status={row.previous_status} />
                    <span aria-hidden="true" className="text-muted-foreground">
                      →
                    </span>
                    <StatusChip status={row.new_status} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    Changed by:{" "}
                    <span className="font-medium text-foreground">
                      {row.actor_email ?? (row.actor_id ? row.actor_id : "System / self-service")}
                    </span>
                  </p>
                  {row.moderator_notes && (
                    <p className="rounded-md border bg-muted/40 p-3 text-foreground">
                      {row.moderator_notes}
                    </p>
                  )}
                  {!testimonialFilter && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-0"
                      onClick={() => setParams({ testimonial: row.testimonial_id })}
                    >
                      View full history
                    </Button>
                  )}
                </CardContent>
              </Card>
            </li>
          ))}
        </ol>
      )}
    </main>
  );
};

export default AdminTestimonialAudit;
